import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function GrievancePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Grievance Redressal Policy</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="h-8 w-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Grievance Redressal Policy</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> January 1, 2025
          </p>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                At Rebuilt, customer satisfaction is our priority. This Grievance Redressal Policy outlines the process for customers to raise complaints, grievances, or concerns regarding our services. We are committed to addressing all grievances fairly, promptly, and transparently.
              </p>
            </section>

            {/* Types of Grievances */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Types of Grievances We Address</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                You may raise a grievance regarding:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Loan application processing delays or rejections</li>
                <li>Disbursement issues or delays</li>
                <li>Incorrect interest calculation or hidden charges</li>
                <li>Payment gateway failures or transaction issues</li>
                <li>Unauthorized debits or wrong payment allocation</li>
                <li>KYC verification problems</li>
                <li>Data privacy and security concerns</li>
                <li>Customer service quality issues</li>
                <li>Refund delays or rejections</li>
                <li>Any other service-related concerns</li>
              </ul>
            </section>

            {/* How to File a Grievance */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">3. How to File a Grievance</h3>
              
              <p className="text-gray-700 font-semibold mb-3">3.1 Step-by-Step Process</p>
              <ol className="list-decimal pl-6 space-y-3 text-gray-700 mb-4">
                <li>
                  <strong>Contact Customer Support (Level 1):</strong>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Email: support@rebuilt.in</li>
                    <li>Phone: +91-XXXX-XXXXXX (Monday to Saturday, 10 AM - 6 PM)</li>
                    <li>In-app chat support</li>
                    <li>Expected resolution time: 24-48 hours</li>
                  </ul>
                </li>
                <li>
                  <strong>Escalate to Grievance Officer (Level 2):</strong>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>If not resolved or unsatisfied with Level 1 response</li>
                    <li>Email: grievance@rebuilt.in</li>
                    <li>Include: Original complaint details, ticket number, desired resolution</li>
                    <li>Expected resolution time: 5-7 business days</li>
                  </ul>
                </li>
                <li>
                  <strong>Senior Management Review (Level 3):</strong>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>If still unresolved after Level 2</li>
                    <li>Email: escalations@rebuilt.in</li>
                    <li>Senior leadership will review and provide final resolution</li>
                    <li>Expected resolution time: 10-15 business days</li>
                  </ul>
                </li>
              </ol>

              <p className="text-gray-700 font-semibold mb-3">3.2 Information to Include in Your Complaint</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Your full name and registered mobile number/email</li>
                <li>Loan application number or customer ID</li>
                <li>Detailed description of the issue</li>
                <li>Date and time of the incident (if applicable)</li>
                <li>Supporting documents (screenshots, transaction receipts, emails)</li>
                <li>Desired resolution or outcome</li>
              </ul>
            </section>

            {/* Grievance Officer Details */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Grievance Officer Contact Details</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                In accordance with the Information Technology Act, 2000 and applicable regulations:
              </p>
              <div className="bg-blue-50 p-6 rounded-lg space-y-2">
                <p className="text-gray-800"><strong>Grievance Officer Name:</strong> [Officer Name]</p>
                <p className="text-gray-800"><strong>Designation:</strong> Customer Grievance Redressal Officer</p>
                <p className="text-gray-800"><strong>Email:</strong> grievance@rebuilt.in</p>
                <p className="text-gray-800"><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p className="text-gray-800"><strong>Address:</strong> [Your registered business address]</p>
                <p className="text-gray-800"><strong>Working Hours:</strong> Monday to Friday, 10:00 AM - 6:00 PM IST</p>
              </div>
            </section>

            {/* Resolution Timeline */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Grievance Resolution Timeline</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Level</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Point of Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Timeline</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Level 1</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Customer Support</td>
                      <td className="px-4 py-3 text-sm text-gray-700">24-48 hours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Level 2</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Grievance Officer</td>
                      <td className="px-4 py-3 text-sm text-gray-700">5-7 business days</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Level 3</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Senior Management</td>
                      <td className="px-4 py-3 text-sm text-gray-700">10-15 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 leading-relaxed mt-4">
                <em>Note: Complex grievances may require additional investigation time. You will be kept informed of progress at each stage.</em>
              </p>
            </section>

            {/* Acknowledgment */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Acknowledgment of Grievance</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>All grievances will be acknowledged within 24 hours of receipt</li>
                <li>You will receive a unique ticket number for tracking</li>
                <li>Acknowledgment will be sent via email and/or SMS</li>
                <li>You can track grievance status through your account dashboard</li>
              </ul>
            </section>

            {/* Resolution Process */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Resolution Process</h3>
              
              <p className="text-gray-700 font-semibold mb-3">7.1 Investigation</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Upon receiving your grievance, we will:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Review your complaint and supporting documents</li>
                <li>Investigate the issue with relevant departments</li>
                <li>Verify facts and identify root cause</li>
                <li>Determine appropriate corrective action</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">7.2 Communication</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Regular updates on investigation progress</li>
                <li>Transparent explanation of findings</li>
                <li>Clear communication of resolution or action taken</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">7.3 Closure</p>
              <p className="text-gray-700 leading-relaxed">
                Once resolved, you will receive a formal closure email detailing the resolution. You may provide feedback on the grievance handling process.
              </p>
            </section>

            {/* Rights and Responsibilities */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Your Rights and Responsibilities</h3>
              
              <p className="text-gray-700 font-semibold mb-3">8.1 Your Rights</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Right to be heard and treated with respect</li>
                <li>Right to timely acknowledgment and resolution</li>
                <li>Right to escalate if unsatisfied with response</li>
                <li>Right to transparent and fair investigation</li>
                <li>Right to receive written response with reasoning</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">8.2 Your Responsibilities</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate and complete information</li>
                <li>Submit supporting documents when requested</li>
                <li>Allow reasonable time for investigation</li>
                <li>Maintain respectful communication</li>
                <li>Use grievance mechanism in good faith</li>
              </ul>
            </section>

            {/* Confidentiality */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Confidentiality</h3>
              <p className="text-gray-700 leading-relaxed">
                All grievances are treated with strict confidentiality. Your personal information and complaint details will only be shared with personnel directly involved in resolving the issue. We do not disclose grievance information to third parties without your consent, except as required by law.
              </p>
            </section>

            {/* No Retaliation */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">10. No Retaliation Policy</h3>
              <p className="text-gray-700 leading-relaxed">
                Rebuilt prohibits any form of retaliation against customers who raise genuine grievances. Filing a complaint will not negatively impact your loan applications, credit assessment, or access to our services.
              </p>
            </section>

            {/* External Remedies */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">11. External Remedies and Regulatory Authorities</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                If your grievance remains unresolved after exhausting our internal redressal mechanism, you may approach:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Reserve Bank of India (RBI) - Banking Ombudsman:</strong>
                  <ul className="list-circle pl-6 mt-1">
                    <li>Website: https://cms.rbi.org.in</li>
                    <li>Email: For respective regional office</li>
                  </ul>
                </li>
                <li>
                  <strong>Consumer Forums:</strong>
                  <ul className="list-circle pl-6 mt-1">
                    <li>District Consumer Disputes Redressal Forum</li>
                    <li>State Consumer Disputes Redressal Commission</li>
                    <li>National Consumer Disputes Redressal Commission</li>
                  </ul>
                </li>
              </ul>
            </section>

            {/* Feedback */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">12. Feedback on Grievance Handling</h3>
              <p className="text-gray-700 leading-relaxed">
                We continuously strive to improve our grievance redressal process. After your grievance is resolved, you will receive a feedback form to rate our service. Your feedback helps us enhance our processes and serve you better.
              </p>
            </section>

            {/* Policy Updates */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">13. Policy Updates</h3>
              <p className="text-gray-700 leading-relaxed">
                This Grievance Redressal Policy may be updated from time to time. Changes will be posted on our website with the updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Contact Summary */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">14. Quick Contact Reference</h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg space-y-3">
                <p className="text-gray-800 font-semibold text-lg">Grievance Redressal Contacts</p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-gray-700 font-medium">Level 1 - Support</p>
                    <p className="text-gray-600 text-sm">support@rebuilt.in</p>
                    <p className="text-gray-600 text-sm">+91-XXXX-XXXXXX</p>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Level 2 - Grievance Officer</p>
                    <p className="text-gray-600 text-sm">grievance@rebuilt.in</p>
                    <p className="text-gray-600 text-sm">+91-XXXX-XXXXXX</p>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Level 3 - Escalations</p>
                    <p className="text-gray-600 text-sm">escalations@rebuilt.in</p>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Payment Issues</p>
                    <p className="text-gray-600 text-sm">payments@rebuilt.in</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Back to Home Button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
